import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';

import {
  ApiError,
  createCard,
  getKycDetails,
  getUserProfile,
  type CardType,
  type CreateCardResult,
} from '../api/client.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import {
  ShippingFields,
  allShippingFields,
  blankShipping,
  buildShippingPayload,
  validateShipping,
  type ShippingFieldErrors,
  type ShippingFormState,
} from '../components/shipping-fields.tsx';
import { showToast } from '../components/toast.tsx';
import {
  isPossiblePhone,
  parseInternationalNumber,
  pastedPhonePatch,
} from '../lib/phone-input.ts';

import { rootRoute } from './__root.tsx';

const CARD_NAME_REGEX = /^[\p{L}\p{N} ]+$/u;
const DIAL_CODE_REGEX = /^\d{1,4}$/;
const PHONE_NUMBER_REGEX = /^\d{4,15}$/;

const humanizeRateLimit = (err: ApiError): string => {
  const retryAfter = typeof err.retryAfter === 'number' ? err.retryAfter : undefined;
  if (retryAfter === undefined) return 'Daily card limit reached. Try again later.';
  if (retryAfter < 60) return 'Daily card limit reached. Try again in a moment.';
  if (retryAfter < 60 * 60) {
    const mins = Math.ceil(retryAfter / 60);
    return `Daily card limit reached. Try again in ${String(mins)} minute${mins === 1 ? '' : 's'}.`;
  }
  if (retryAfter < 60 * 60 * 24) {
    const hours = Math.ceil(retryAfter / 3600);
    return `Daily card limit reached. Try again in ${String(hours)} hour${hours === 1 ? '' : 's'}.`;
  }
  return 'Daily card limit reached. Try again tomorrow.';
};

interface FormState {
  type: CardType;
  name: string;
  dialCode: string;
  phoneNumber: string;
}

interface FieldErrors {
  name?: string;
  dialCode?: string;
  phoneNumber?: string;
}

function validateCard(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (form.name !== '' && !CARD_NAME_REGEX.test(form.name)) {
    errors.name = 'Letters, numbers, and spaces only.';
  }
  if (form.name !== '' && (form.name.length < 2 || form.name.length > 27)) {
    errors.name = '2 to 27 characters.';
  }
  if (!DIAL_CODE_REGEX.test(form.dialCode)) {
    errors.dialCode = '1 to 4 digits.';
  }
  if (!PHONE_NUMBER_REGEX.test(form.phoneNumber)) {
    errors.phoneNumber = 'Digits only, 4 to 15.';
  } else if (DIAL_CODE_REGEX.test(form.dialCode) && !isPossiblePhone(form.dialCode, form.phoneNumber)) {
    // Catch wrong-length / mis-pasted numbers here so the provider does not
    // reject the card creation with an opaque "invalid phone number".
    errors.phoneNumber = 'Enter a valid phone number for this dial code.';
  }
  return errors;
}

const NewCardPage = (): ReactNode => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>({
    type: 'VIRTUAL',
    name: '',
    dialCode: '',
    phoneNumber: '',
  });
  const [touched, setTouched] = useState<Record<keyof FieldErrors, boolean>>({
    name: false,
    dialCode: false,
    phoneNumber: false,
  });
  const [shipping, setShipping] = useState<ShippingFormState>(blankShipping);
  const [shippingTouched, setShippingTouched] = useState<
    Partial<Record<keyof ShippingFieldErrors, boolean>>
  >({});
  const [kycPrefilled, setKycPrefilled] = useState(false);
  const [profilePrefilled, setProfilePrefilled] = useState(false);
  const [phonePrefilled, setPhonePrefilled] = useState(false);
  const [shippingOpen, setShippingOpen] = useState(false);
  const shippingDetailsRef = useRef<HTMLDetailsElement>(null);

  const kycAddressQuery = useQuery({
    queryKey: ['kycAddress'],
    queryFn: ({ signal }) => getKycDetails(signal),
    enabled: form.type === 'PHYSICAL',
    staleTime: 5 * 60 * 1000,
  });

  // Fetched for both card types: the profile carries the cardholder's phone,
  // used to prefill the OTP phone fields below (and the shipping email/recipient
  // for physical cards).
  const userProfileQuery = useQuery({
    queryKey: ['userProfile'],
    queryFn: ({ signal }) => getUserProfile(signal),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (kycPrefilled || kycAddressQuery.data === undefined) return;
    const a = kycAddressQuery.data;
    setShipping((prev) => ({
      ...prev,
      firstName: a.firstName ?? prev.firstName,
      lastName: a.lastName ?? prev.lastName,
      line1: a.residentialAddressLine1 ?? prev.line1,
      line2: a.residentialAddressLine2 ?? prev.line2,
      city: a.residentialAddressCity ?? prev.city,
      postalCode: a.residentialAddressPostalCode ?? prev.postalCode,
      country: a.residentialAddressCountry ?? prev.country,
    }));
    setKycPrefilled(true);
  }, [kycAddressQuery.data, kycPrefilled]);

  useEffect(() => {
    if (profilePrefilled || userProfileQuery.data === undefined) return;
    const p = userProfileQuery.data;
    setShipping((prev) => ({ ...prev, email: p.email }));
    setProfilePrefilled(true);
  }, [userProfileQuery.data, profilePrefilled]);

  // Prefill the OTP phone from the cardholder's profile so they don't retype
  // it. Runs once and only fills fields the user has not already edited, so a
  // late-arriving profile never clobbers in-progress input.
  useEffect(() => {
    if (phonePrefilled || userProfileQuery.data === undefined) return;
    const p = userProfileQuery.data;
    const dial = p.phoneDialCode ?? '';
    const number = p.phoneNumber ?? '';
    if (dial !== '' || number !== '') {
      setForm((prev) => ({
        ...prev,
        dialCode: touched.dialCode ? prev.dialCode : dial !== '' ? dial : prev.dialCode,
        phoneNumber: touched.phoneNumber ? prev.phoneNumber : number !== '' ? number : prev.phoneNumber,
      }));
    }
    setPhonePrefilled(true);
  }, [userProfileQuery.data, phonePrefilled, touched.dialCode, touched.phoneNumber]);

  // The recipient is almost always the cardholder, who just entered their
  // phone for card creation above — mirror it into the shipping phone until
  // the user edits the shipping phone directly.
  useEffect(() => {
    if (form.type !== 'PHYSICAL') return;
    if (shippingTouched.recipientDialCode === true && shippingTouched.recipientPhone === true) {
      return;
    }
    setShipping((prev) => {
      const next = { ...prev };
      if (shippingTouched.recipientDialCode !== true) next.recipientDialCode = form.dialCode;
      if (shippingTouched.recipientPhone !== true) next.recipientPhone = form.phoneNumber;
      return next;
    });
  }, [
    form.type,
    form.dialCode,
    form.phoneNumber,
    shippingTouched.recipientDialCode,
    shippingTouched.recipientPhone,
  ]);

  const cardErrors = validateCard(form);
  const shippingErrors = form.type === 'PHYSICAL' ? validateShipping(shipping) : {};
  const hasErrors =
    Object.keys(cardErrors).length > 0 ||
    (form.type === 'PHYSICAL' && Object.keys(shippingErrors).length > 0);

  const create = useMutation({
    mutationFn: (): Promise<CreateCardResult> =>
      createCard({
        type: form.type,
        ...(form.name !== '' && { name: form.name.trim() }),
        dialCode: form.dialCode,
        phoneNumber: form.phoneNumber,
        ...(form.type === 'PHYSICAL' && { shipping: buildShippingPayload(shipping) }),
      }),
    onSuccess: (result): void => {
      void qc.invalidateQueries({ queryKey: ['cards'] });
      if (result.shippingError !== undefined) {
        showToast(`Card created. Shipping failed: ${result.shippingError.message}`);
      }
      void navigate({ to: '/', search: { cardId: result.id } });
    },
    onError: (err: unknown): void => {
      if (err instanceof ApiError) {
        if (err.code === 'CARD_CREATE_LIMIT_EXCEEDED') {
          showToast(humanizeRateLimit(err));
          return;
        }
        if (err.code === 'CARD_CREATE_UNCERTAIN') {
          showToast(
            "We're not sure if your card was created. Check your cards before trying again.",
          );
          void qc.refetchQueries({ queryKey: ['cards'] }).then(() => {
            void navigate({ to: '/' });
          });
          return;
        }
        if (err.code === 'CARD_CREATE_TIMEOUT') {
          showToast('Connection was slow. Safe to try again.');
          return;
        }
        showToast(err.message || 'Could not create card. Try again.');
        return;
      }
      showToast('Could not create card. Try again.');
    },
  });

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setTouched({ name: true, dialCode: true, phoneNumber: true });
    if (form.type === 'PHYSICAL') {
      setShippingTouched(() => {
        const all: Partial<Record<keyof ShippingFieldErrors, boolean>> = {};
        for (const f of allShippingFields) all[f] = true;
        return all;
      });
    }
    // An invalid submit must reveal the collapsed shipping errors, otherwise
    // the create button looks dead — the no-op below hides the highlighted fields.
    if (form.type === 'PHYSICAL' && Object.keys(shippingErrors).length > 0) {
      setShippingOpen(true);
      shippingDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (hasErrors || create.isPending) return;
    create.mutate();
  };

  const showError = (field: keyof FieldErrors): string | undefined =>
    touched[field] || create.isError ? cardErrors[field] : undefined;

  const showShippingError = (field: keyof ShippingFieldErrors): string | undefined =>
    shippingTouched[field] || create.isError ? shippingErrors[field] : undefined;

  return (
    <div className="screen">
      <Topbar />
      <main className="new-card-main">
        <form className="new-card-form" onSubmit={handleSubmit} noValidate>
          <fieldset className="card-type-fieldset">
            <legend>Card type</legend>
            <div className="card-type-grid">
              <label className={`card-type-tile${form.type === 'VIRTUAL' ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="card-type"
                  className="card-type-radio-input"
                  value="VIRTUAL"
                  checked={form.type === 'VIRTUAL'}
                  onChange={(): void => {
                    setForm((s) => ({ ...s, type: 'VIRTUAL' }));
                  }}
                  aria-labelledby="card-type-virtual-title"
                  aria-describedby="card-type-virtual-desc"
                />
                <span className="card-type-tile-head">
                  <span id="card-type-virtual-title" className="card-type-tile-title">
                    Virtual
                  </span>
                  <span className="card-type-radio" aria-hidden="true" />
                </span>
                <span id="card-type-virtual-desc" className="card-type-tile-desc">
                  Use immediately online
                </span>
              </label>
              <label className={`card-type-tile${form.type === 'PHYSICAL' ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="card-type"
                  className="card-type-radio-input"
                  value="PHYSICAL"
                  checked={form.type === 'PHYSICAL'}
                  onChange={(): void => {
                    setForm((s) => ({ ...s, type: 'PHYSICAL' }));
                  }}
                  aria-labelledby="card-type-physical-title"
                  aria-describedby="card-type-physical-desc"
                />
                <span className="card-type-tile-head">
                  <span id="card-type-physical-title" className="card-type-tile-title">
                    Physical
                  </span>
                  <span className="card-type-radio" aria-hidden="true" />
                </span>
                <span id="card-type-physical-desc" className="card-type-tile-desc">
                  Delivered by post
                </span>
              </label>
            </div>
          </fieldset>

          <label className="new-card-field" htmlFor="card-name">
            <span className="new-card-field-label">Name (optional)</span>
            <input
              id="card-name"
              type="text"
              maxLength={27}
              value={form.name}
              onChange={(e): void => {
                setForm((s) => ({ ...s, name: e.target.value }));
              }}
              onBlur={(): void => {
                setTouched((t) => ({ ...t, name: true }));
              }}
              aria-invalid={showError('name') !== undefined}
              aria-describedby={showError('name') !== undefined ? 'card-name-hint' : undefined}
            />
            {showError('name') !== undefined ? (
              <span id="card-name-hint" className="new-card-field-hint">
                {showError('name')}
              </span>
            ) : null}
          </label>

          <div className="new-card-phone">
            <label className="new-card-field new-card-field-dial" htmlFor="card-dial-code">
              <span className="new-card-field-label">Dial code</span>
              <input
                id="card-dial-code"
                type="tel"
                /* inputMode "tel" (not "numeric"): the numeric keypad on some
                   Android keyboards swallows a clipboard paste, so the field
                   never receives the pasted number. The tel keypad accepts it. */
                inputMode="tel"
                /* Roomy enough that a pasted full international number reaches
                   onChange intact instead of being clipped to 4 chars before
                   the split runs; the value is re-capped to 4 digits below. */
                maxLength={18}
                placeholder="44"
                autoComplete="tel-country-code"
                value={form.dialCode}
                onPaste={(e): void => {
                  e.preventDefault();
                  const patch = pastedPhonePatch(e.clipboardData.getData('text'), 'dialCode');
                  setForm((s) => ({ ...s, ...patch }));
                  setTouched((t) => ({
                    ...t,
                    ...(patch.dialCode !== undefined ? { dialCode: true } : {}),
                    ...(patch.phoneNumber !== undefined ? { phoneNumber: true } : {}),
                  }));
                }}
                onChange={(e): void => {
                  const split = parseInternationalNumber(e.target.value);
                  if (split !== null) {
                    setForm((s) => ({ ...s, ...split }));
                    setTouched((t) => ({ ...t, dialCode: true, phoneNumber: true }));
                    return;
                  }
                  setForm((s) => ({
                    ...s,
                    dialCode: e.target.value.replace(/\D/g, '').slice(0, 4),
                  }));
                }}
                onBlur={(): void => {
                  setTouched((t) => ({ ...t, dialCode: true }));
                }}
                aria-invalid={showError('dialCode') !== undefined}
                aria-describedby={
                  showError('dialCode') !== undefined ? 'card-dial-code-hint' : undefined
                }
                aria-required
              />
              {showError('dialCode') !== undefined ? (
                <span id="card-dial-code-hint" className="new-card-field-hint">
                  {showError('dialCode')}
                </span>
              ) : null}
            </label>
            <label className="new-card-field new-card-field-phone" htmlFor="card-phone-number">
              <span className="new-card-field-label">Phone number</span>
              <input
                id="card-phone-number"
                type="tel"
                /* inputMode "tel" (not "numeric") so a clipboard paste is not
                   swallowed by the numeric keypad on some Android keyboards. */
                inputMode="tel"
                /* Holds a full pasted international number so the split can run
                   before the national digits are re-capped to 15 below. */
                maxLength={20}
                placeholder="7700900000"
                autoComplete="tel-national"
                value={form.phoneNumber}
                onPaste={(e): void => {
                  e.preventDefault();
                  const patch = pastedPhonePatch(e.clipboardData.getData('text'), 'phoneNumber');
                  setForm((s) => ({ ...s, ...patch }));
                  setTouched((t) => ({
                    ...t,
                    ...(patch.dialCode !== undefined ? { dialCode: true } : {}),
                    ...(patch.phoneNumber !== undefined ? { phoneNumber: true } : {}),
                  }));
                }}
                onChange={(e): void => {
                  const split = parseInternationalNumber(e.target.value);
                  if (split !== null) {
                    setForm((s) => ({ ...s, ...split }));
                    setTouched((t) => ({ ...t, dialCode: true, phoneNumber: true }));
                    return;
                  }
                  setForm((s) => ({
                    ...s,
                    phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 15),
                  }));
                }}
                onBlur={(): void => {
                  setTouched((t) => ({ ...t, phoneNumber: true }));
                }}
                aria-invalid={showError('phoneNumber') !== undefined}
                aria-describedby={
                  showError('phoneNumber') !== undefined ? 'card-phone-number-hint' : undefined
                }
                aria-required
              />
              {showError('phoneNumber') !== undefined ? (
                <span id="card-phone-number-hint" className="new-card-field-hint">
                  {showError('phoneNumber')}
                </span>
              ) : null}
            </label>
          </div>

          {form.type === 'PHYSICAL' ? (
            <details
              ref={shippingDetailsRef}
              className="shipping-section"
              open={shippingOpen}
              onToggle={(e): void => {
                setShippingOpen(e.currentTarget.open);
              }}
            >
              <summary className="shipping-summary">
                Shipping info
                {kycAddressQuery.isLoading ? (
                  <span className="shipping-loading"> (loading…)</span>
                ) : null}
              </summary>
              <div className="shipping-fields">
                <ShippingFields
                  shipping={shipping}
                  onPatch={(patch): void => {
                    setShipping((s) => ({ ...s, ...patch }));
                  }}
                  onTouch={(...fields): void => {
                    setShippingTouched((t) => {
                      const next = { ...t };
                      for (const f of fields) next[f] = true;
                      return next;
                    });
                  }}
                  showError={showShippingError}
                />
              </div>
            </details>
          ) : null}

          <button type="submit" className="new-card-submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create card'}
          </button>
        </form>
      </main>
    </div>
  );
};

const Topbar = (): ReactNode => {
  const navigate = useNavigate();
  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-back"
        aria-label="Back"
        onClick={(): void => {
          void navigate({ to: '/' });
        }}
      >
        <IconChevronLeft />
      </button>
      <h1 className="topbar-title">New card</h1>
      <span className="topbar-spacer" aria-hidden="true" />
    </header>
  );
};

export const newCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/new-card',
  component: NewCardPage,
});
